/**
 * DebugPanel.tsx — In-game developer console
 * Toggle with ` (backtick). Real game studios use exactly this kind of tool.
 */
import React, { useState, useEffect } from 'react';
import type { GameState } from '../game/types';
import { getActiveBullshiiii } from '../game/bunchaBullshiiii';
import { ENEMY_DATABASE } from '../game/enemies';
import {
  debugGodMode,          setDebugGodMode,
  debugNavOverlay,       setDebugNavOverlay,
  debugShowWaypoints,    setDebugShowWaypoints,
  debugFlowField,        setDebugFlowField,
  debugFlowFieldMode,    setDebugFlowFieldMode,
  debugDynamicLighting,  setDebugDynamicLighting,
  debugWeaponSockets,    setDebugWeaponSockets,
  debugEnemyHpMult,      setDebugEnemyHpMult,
  debugEnemyDamageMult,  setDebugEnemyDamageMult,
  debugPlayerSpeedMult,  setDebugPlayerSpeedMult,
  debugPlayerDamageMult, setDebugPlayerDamageMult,
  debugTimeScale,        setDebugTimeScale,
  logDebug, getDebugLog, downloadDebugCsv,
} from '../game/debugState';
import { debugGiveItem } from '../game/itemsAdapter';
import { debugSpawnEnemy, debugKillAll } from '../game/enemyAdapter';
import { isBotEnabled, getBotRunLogs, printBotReport, toggleBot, exportBotData, getBotBrain, resetBotBrain } from '../game/autoPlaytester';

type Tab = 'nav' | 'char' | 'enemies' | 'data';

interface Props {
  stateRef: React.RefObject<GameState | null>;
}

const RARITY_COLOR: Record<string, string> = {
  common: '#9e9e9e', uncommon: '#4caf50', rare: '#2196f3', epic: '#9c27b0', legendary: '#ff9800',
};

const ENEMY_IDS = Object.keys(ENEMY_DATABASE);

// ─── Tiny sub-components ──────────────────────────────────────────────────────

function Toggle({ label, value, onChange }: { label: string; value: boolean; onChange: () => void }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 4 }}>
      <div onClick={onChange} style={{
        width: 36, height: 18, borderRadius: 9, flexShrink: 0, cursor: 'pointer',
        background: value ? '#00cc88' : '#2a2a2a', position: 'relative', transition: 'background 0.15s',
      }}>
        <div style={{
          position: 'absolute', top: 2, left: value ? 18 : 2, width: 14, height: 14,
          borderRadius: 7, background: '#fff', transition: 'left 0.15s',
        }} />
      </div>
      <span style={{ color: value ? '#eee' : '#666', fontSize: 11 }}>{label}</span>
    </label>
  );
}

function Slider({ label, min, max, step, value, onChange, unit = '×' }: {
  label: string; min: number; max: number; step: number; value: number;
  onChange: (v: number) => void; unit?: string;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
      <span style={{ color: '#888', fontSize: 11, width: 90, flexShrink: 0 }}>{label}</span>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(Number(e.target.value))}
        style={{ flex: 1, accentColor: '#00ffcc', cursor: 'pointer' }} />
      <span style={{ color: '#00ffcc', fontSize: 11, width: 40, textAlign: 'right' }}>
        {value.toFixed(step < 0.1 ? 2 : step < 1 ? 1 : 0)}{unit}
      </span>
    </div>
  );
}

function Btn({ label, color = '#444', textColor = '#ccc', onClick }: {
  label: string; color?: string; textColor?: string; onClick: () => void;
}) {
  return (
    <button onClick={onClick} style={{
      background: color, border: `1px solid ${textColor}44`, color: textColor,
      padding: '5px 8px', borderRadius: 4, cursor: 'pointer',
      fontFamily: 'inherit', fontSize: 11, marginBottom: 3, width: '100%', textAlign: 'left',
    }}>{label}</button>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div style={{ color: '#555', fontSize: 10, letterSpacing: 1, marginTop: 10, marginBottom: 4, borderBottom: '1px solid #1e1e1e', paddingBottom: 2 }}>{children}</div>;
}

// ─── Main Panel ───────────────────────────────────────────────────────────────

export default function DebugPanel({ stateRef }: Props) {
  const [tab, setTab]             = useState<Tab>('nav');
  const [tick, setTick]           = useState(0);
  const [itemSearch, setItemSearch] = useState('');

  // Local copies of debug flags (so React re-renders on change)
  const [godMode,    setGodModeL]    = useState(debugGodMode);
  const [navOv,      setNavOvL]      = useState(debugNavOverlay);
  const [wpOv,       setWpOvL]       = useState(debugShowWaypoints);
  const [flowOv,     setFlowOvL]     = useState(debugFlowField);
  const [flowMode,   setFlowModeL]   = useState<'arrows' | 'heat' | 'cost'>(debugFlowFieldMode);
  const [dynLight,   setDynLightL]   = useState(debugDynamicLighting);
  const [wpnSockets, setWpnSocketsL] = useState(debugWeaponSockets);
  const [enemyHp,    setEnemyHpL]    = useState(debugEnemyHpMult);
  const [enemyDmg,   setEnemyDmgL]   = useState(debugEnemyDamageMult);
  const [speedMult,  setSpeedMultL]  = useState(debugPlayerSpeedMult);
  const [damageMult, setDamageMultL] = useState(debugPlayerDamageMult);
  const [timeScale,  setTimeScaleL]  = useState(debugTimeScale);

  // Poll for live state every 400ms
  useEffect(() => {
    const id = setInterval(() => setTick(n => n + 1), 400);
    return () => clearInterval(id);
  }, []);

  const st = stateRef.current;
  const player = st?.player;
  const log = getDebugLog();
  const allItems = getActiveBullshiiii();
  const filtered = itemSearch
    ? allItems.filter(i => i.name.toLowerCase().includes(itemSearch.toLowerCase()))
    : allItems;

  void tick; // suppress unused var warning — used to trigger refresh

  const TABS: { id: Tab; icon: string }[] = [
    { id: 'nav',     icon: '🗺️ NAV' },
    { id: 'char',    icon: '🧑 CHAR' },
    { id: 'enemies', icon: '👾 ENEMIES' },
    { id: 'data',    icon: '📊 DATA' },
  ];

  return (
    <div style={{
      position: 'absolute', top: 12, right: 12,
      width: 300, maxHeight: 'calc(100vh - 40px)',
      background: 'rgba(6,6,10,0.97)',
      border: '1.5px solid #00ffcc',
      borderRadius: 8,
      fontFamily: '"Share Tech Mono", monospace',
      fontSize: 12,
      color: '#ccc',
      display: 'flex', flexDirection: 'column',
      zIndex: 9999,
      boxShadow: '0 0 30px rgba(0,255,200,0.25)',
      userSelect: 'none',
      pointerEvents: 'all',
    }}>

      {/* Header */}
      <div style={{
        padding: '7px 12px', borderBottom: '1px solid #1e1e1e',
        color: '#00ffcc', fontSize: 12, fontWeight: 'bold',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span>⚙ DEV CONSOLE</span>
        <span style={{ color: '#444', fontSize: 10 }}>` to close</span>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #1e1e1e' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            flex: 1, padding: '5px 2px',
            background: tab === t.id ? 'rgba(0,255,200,0.07)' : 'transparent',
            border: 'none',
            borderBottom: `2px solid ${tab === t.id ? '#00ffcc' : 'transparent'}`,
            color: tab === t.id ? '#00ffcc' : '#555',
            cursor: 'pointer', fontFamily: 'inherit', fontSize: 10,
          }}>{t.icon}</button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 10px' }}>

        {/* ── NAV TAB ── */}
        {tab === 'nav' && (
          <div>
            <SectionLabel>OVERLAY TOGGLES</SectionLabel>
            <Toggle label="Room walls + gaps" value={navOv} onChange={() => {
              const v = !navOv; setDebugNavOverlay(v); setNavOvL(v);
            }} />
            <Toggle label="Corner triangles (part of walls)" value={navOv} onChange={() => {
              const v = !navOv; setDebugNavOverlay(v); setNavOvL(v);
            }} />
            <Toggle label="Enemy waypoint arrows" value={wpOv} onChange={() => {
              const v = !wpOv; setDebugShowWaypoints(v); setWpOvL(v);
            }} />
            <Toggle label="Flow Field overlay" value={flowOv} onChange={() => {
              const v = !flowOv; setDebugFlowField(v); setFlowOvL(v);
            }} />
            {flowOv && (
              <div style={{ marginLeft: 12, marginBottom: 6 }}>
                {(['arrows', 'heat', 'cost'] as const).map(m => (
                  <label key={m} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 10, cursor: 'pointer' }}>
                    <input type="radio" name="ffMode" checked={flowMode === m} onChange={() => { setDebugFlowFieldMode(m); setFlowModeL(m); }} style={{ accentColor: '#00ffcc' }} />
                    <span style={{ color: flowMode === m ? '#0ff' : '#666', fontSize: 10 }}>{m}</span>
                  </label>
                ))}
              </div>
            )}
            {/* BETA 20.95 — fake dynamic lighting (off by default, experimental look). */}
            <Toggle label="🔦 Dynamic lighting (experimental)" value={dynLight} onChange={() => {
              const v = !dynLight; setDebugDynamicLighting(v); setDynLightL(v);
            }} />
            {/* BETA 22.69 — weapon socket overlay: red=muzzle, blue=pivot, line=aim */}
            <Toggle label="🔫 Weapon sockets (red=muzzle blue=pivot)" value={wpnSockets} onChange={() => {
              const v = !wpnSockets; setDebugWeaponSockets(v); setWpnSocketsL(v);
            }} />

            <SectionLabel>LIVE ROOM INFO</SectionLabel>
            {player ? (
              <div style={{ lineHeight: 1.8, color: '#999' }}>
                Player: <span style={{ color: '#0ff' }}>{Math.round(player.x)}, {Math.round(player.y)}</span><br />
                Room: <span style={{ color: '#0ff' }}>{(player as any).currentRoom ? (player as any).currentRoom.id ?? 'YES' : '— corridor'}</span><br />
                Rooms on map: <span style={{ color: '#0ff' }}>{st?.map?.roomMap?.rooms?.length ?? 0}</span>
              </div>
            ) : <div style={{ color: '#444' }}>Not in game</div>}

            <SectionLabel>WHAT THE COLORS MEAN</SectionLabel>
            <div style={{ color: '#666', fontSize: 10, lineHeight: 1.8 }}>
              🔴 Red = solid wall (can't pass)<br />
              🟡 Yellow ○ = gap center enemy aims for<br />
              🔵 Blue △ = corner funnel zone<br />
              🟢 Green → = enemy waypoint arrow
            </div>
          </div>
        )}

        {/* ── CHARACTER TAB ── */}
        {tab === 'char' && (
          <div>
            <Toggle label="☠ GOD MODE (no damage)" value={godMode} onChange={() => {
              const v = !godMode; setDebugGodMode(v); setGodModeL(v);
              logDebug('god_mode', v ? 'ON' : 'OFF');
            }} />

            <SectionLabel>PLAYER LIVE STATS</SectionLabel>
            {player ? (
              <div style={{ background: '#0a0a0e', padding: 6, borderRadius: 4, lineHeight: 1.8, color: '#999' }}>
                HP: <span style={{ color: '#f55' }}>{Math.round(player.hp)}/{player.maxHp}</span>
                &nbsp;&nbsp;Lv: <span style={{ color: '#ff0' }}>{player.level}</span>
                &nbsp;&nbsp;Gold: <span style={{ color: '#ffd700' }}>{player.gold ?? 0}</span><br />
                Speed: <span style={{ color: '#0f0' }}>{Math.round(player.speed)}</span>
                &nbsp;&nbsp;Kills: <span style={{ color: '#f0f' }}>{st?.killCount ?? 0}</span>
              </div>
            ) : <div style={{ color: '#444' }}>Not in game</div>}

            <SectionLabel>⏩ GAME SPEED</SectionLabel>
            <Slider label="Time scale" min={0.25} max={8} step={0.25} value={timeScale} onChange={v => {
              setDebugTimeScale(v); setTimeScaleL(v);
              logDebug('time_scale', v);
            }} />
            <div style={{ color: timeScale >= 4 ? '#fa0' : timeScale <= 0.5 ? '#88f' : '#555', fontSize: 10, marginBottom: 6 }}>
              {timeScale <= 0.25 ? '🐌 Slow-mo' : timeScale <= 0.5 ? '🐢 Slow' : timeScale === 1 ? '⚖ Normal' : timeScale <= 2 ? '⚡ Fast' : timeScale <= 4 ? '🚀 Very fast' : '☄️ Ludicrous speed'}
            </div>
            <Btn label="Reset to 1×" color="#1a1a0a" textColor="#888" onClick={() => {
              setDebugTimeScale(1); setTimeScaleL(1);
            }} />

            <SectionLabel>PLAYER SPEED</SectionLabel>
            <Slider label="Speed mult" min={0.5} max={5} step={0.5} value={speedMult} onChange={v => {
              setDebugPlayerSpeedMult(v); setSpeedMultL(v);
            }} />

            <SectionLabel>PLAYER DAMAGE</SectionLabel>
            <Slider label="Damage mult" min={1} max={25} step={1} value={damageMult} onChange={v => {
              setDebugPlayerDamageMult(v); setDamageMultL(v);
              logDebug('player_damage_mult', v);
            }} />

            <SectionLabel>GIVE ITEM</SectionLabel>
            <input
              value={itemSearch}
              onChange={e => setItemSearch(e.target.value)}
              placeholder="Search items..."
              style={{
                width: '100%', boxSizing: 'border-box', background: '#111', border: '1px solid #333',
                color: '#ddd', padding: '4px 7px', borderRadius: 4, fontFamily: 'inherit', fontSize: 11, marginBottom: 4,
              }}
            />
            <div style={{ maxHeight: 170, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {filtered.slice(0, 40).map(item => (
                <button key={item.id} onClick={() => {
                  debugGiveItem(item.id);
                  logDebug('gave_item', item.name);
                }} style={{
                  textAlign: 'left', background: '#10100e', border: '1px solid #2a2a2a',
                  color: '#bbb', padding: '3px 7px', borderRadius: 3,
                  cursor: 'pointer', fontFamily: 'inherit', fontSize: 11,
                }}>
                  <span style={{ color: RARITY_COLOR[item.rarity] ?? '#888' }}>●</span> {item.name}
                  <span style={{ color: '#444', fontSize: 9, marginLeft: 4 }}>{item.rarity}</span>
                </button>
              ))}
              {filtered.length === 0 && <div style={{ color: '#444', fontSize: 11 }}>No items match</div>}
            </div>
          </div>
        )}

        {/* ── ENEMIES TAB ── */}
        {tab === 'enemies' && (
          <div>
            <SectionLabel>ENEMY HP MULTIPLIER</SectionLabel>
            <Slider label="HP mult" min={0.05} max={3} step={0.05} value={enemyHp} onChange={v => {
              setDebugEnemyHpMult(v); setEnemyHpL(v);
              logDebug('enemy_hp_mult', v);
            }} />
            <div style={{ color: enemyHp <= 0.15 ? '#f55' : enemyHp >= 2 ? '#fa0' : '#555', fontSize: 10, marginBottom: 6 }}>
              {enemyHp <= 0.15 ? '💀 One-shot mode' : enemyHp <= 0.5 ? '⚡ Glass enemies' : enemyHp >= 2 ? '🛡 Tank mode' : '⚖ Normal'}
            </div>

            {/* BETA 20.91 — live "how hard do they hit" knob (the missing difficulty dial). */}
            <SectionLabel>ENEMY DAMAGE MULTIPLIER</SectionLabel>
            <Slider label="DMG mult" min={0.1} max={5} step={0.1} value={enemyDmg} onChange={v => {
              setDebugEnemyDamageMult(v); setEnemyDmgL(v);
              logDebug('enemy_damage_mult', v);
            }} />
            <div style={{ color: enemyDmg <= 0.3 ? '#5f5' : enemyDmg >= 2 ? '#f55' : '#555', fontSize: 10, marginBottom: 6 }}>
              {enemyDmg <= 0.3 ? '🍼 Pillow fists' : enemyDmg <= 0.7 ? '😌 Forgiving' : enemyDmg >= 2 ? '☠ Brutal' : '⚖ Normal'}
            </div>

            <Btn label="☠ KILL ALL ENEMIES" color="#2a0000" textColor="#f55" onClick={() => {
              debugKillAll();
              logDebug('kill_all', 'triggered');
            }} />

            <SectionLabel>SPAWN ENEMY (200wu right of player)</SectionLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 220, overflowY: 'auto' }}>
              {ENEMY_IDS.map(id => (
                <button key={id} onClick={() => {
                  if (!player) return;
                  debugSpawnEnemy(id, player.x + 220, player.y);
                  logDebug('spawned', id);
                }} style={{
                  textAlign: 'left', background: '#0e0800', border: '1px solid #2a2a2a',
                  color: '#f88', padding: '3px 8px', borderRadius: 3,
                  cursor: 'pointer', fontFamily: 'inherit', fontSize: 11,
                }}>▶ {id}</button>
              ))}
            </div>
          </div>
        )}

        {/* ── DATA TAB ── */}
        {tab === 'data' && (
          <div>
            <SectionLabel>LIVE RUN STATS</SectionLabel>
            {st && player ? (
              <div style={{ background: '#0a0a0e', padding: 6, borderRadius: 4, lineHeight: 1.8, color: '#999' }}>
                Kills: <span style={{ color: '#f0f' }}>{st.killCount ?? 0}</span><br />
                Time: <span style={{ color: '#0ff' }}>
                  {Math.floor((st.time ?? 0) / 60)}:{String(Math.floor((st.time ?? 0) % 60)).padStart(2, '0')}
                </span><br />
                Wave: <span style={{ color: '#ff0' }}>{st.wave ?? 1}</span><br />
                Phase: <span style={{ color: '#0f0' }}>{st.phase}</span><br />
                Chests opened: <span style={{ color: '#ffd700' }}>{st.chestsOpenedThisRun ?? 0}</span>
              </div>
            ) : <div style={{ color: '#444' }}>Not in game</div>}

            <SectionLabel>🤖 AUTO-PLAYTESTER BOT</SectionLabel>
            <div style={{ background: '#0a0a0e', padding: 6, borderRadius: 4, lineHeight: 1.7, color: '#999', marginBottom: 6, fontSize: 11 }}>
              Status: <span style={{ color: isBotEnabled() ? '#0f0' : '#f44', fontWeight: 'bold' }}>
                {isBotEnabled() ? '● RUNNING' : '○ OFF'}
              </span>&nbsp;&nbsp;
              Runs this session: <span style={{ color: '#0ff' }}>{getBotRunLogs().length}</span>
              {(() => {
                const b = getBotBrain();
                if (!b) return null;
                return (<>
                  <br />
                  <span style={{ color: '#888' }}>🧠 BRAIN ({b.totalRunsLearned} runs learned)</span><br />
                  Avg survival: <span style={{ color: '#ff0' }}>{b.avgSurvivalSec.toFixed(0)}s</span>&nbsp;
                  Best: <span style={{ color: '#0f0' }}>{b.bestSurvivalSec.toFixed(0)}s</span><br />
                  Flee @ <span style={{ color: '#f88' }}>{(b.fleeThreshold * 100).toFixed(0)}% HP</span>&nbsp;
                  Heal @ <span style={{ color: '#8f8' }}>{(b.healSeekThreshold * 100).toFixed(0)}% HP</span>&nbsp;
                  Kite <span style={{ color: '#88f' }}>{(b.kiteAggression * 100).toFixed(0)}%</span><br />
                  Mode: <span style={{ color: b.preferOffense ? '#f80' : b.preferDefense ? '#08f' : '#888' }}>
                    {b.preferOffense ? '⚔ OFFENSE' : b.preferDefense ? '🛡 DEFENSE' : 'balanced'}
                  </span>&nbsp;
                  {b.avoidBossShrines && <span style={{ color: '#f44' }}>⚠ avoiding boss shrines</span>}
                </>);
              })()}
            </div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
              <button
                onClick={() => { const on = toggleBot(); logDebug('bot_toggle', on ? 'enabled' : 'disabled'); }}
                style={{ flex: 1, background: isBotEnabled() ? '#1a0a0a' : '#0a1a0a', border: `1px solid ${isBotEnabled() ? '#f44' : '#0f0'}`, color: isBotEnabled() ? '#f44' : '#0f0', borderRadius: 4, padding: '4px 0', fontFamily: 'monospace', fontSize: 11, cursor: 'pointer' }}
              >
                {isBotEnabled() ? 'DISABLE BOT (F9)' : 'ENABLE BOT (F9)'}
              </button>
              <button
                onClick={printBotReport}
                style={{ flex: 1, background: '#0a0a1a', border: '1px solid #44f', color: '#aaf', borderRadius: 4, padding: '4px 0', fontFamily: 'monospace', fontSize: 11, cursor: 'pointer' }}
              >
                REPORT (F10)
              </button>
              <button
                onClick={() => { if (confirm('Reset bot brain? This clears all learning.')) resetBotBrain(); }}
                style={{ flex: 1, background: '#1a0a0a', border: '1px solid #844', color: '#a66', borderRadius: 4, padding: '4px 0', fontFamily: 'monospace', fontSize: 11, cursor: 'pointer' }}
              >
                RESET BRAIN
              </button>
            </div>
            <button
              onClick={() => {
                const runs = getBotRunLogs();
                if (runs.length === 0) { alert('No bot runs yet!'); return; }
                // Build CSV
                // BETA 13.59: added build, blineTriggers, trailTeleports, namedEliteFlees columns
                // BETA 13.60: added perf buckets (ms/sec) — botMs, enemiesMs, weaponsMs, itemsMs
                // BETA 13.70: added pickEffect columns — totalPicksTracked, brokenPickCount, brokenPicks (which picks had no effect)
                // BETA 13.73: added `character` column so shared-build characters (cowboy/killa/viking on rpk_suppressor; tactical/lebron on peanut_sniper) can be analyzed separately
                // BETA 13.80: added trapMarks (Fix 1 wire-up firings), enemyNearMissFrames (frames any enemy was hugging player without dealing damage), weaponNearMisses (projectiles that came close but never hit)
                // BETA 13.82: added noveltyPicks (picks of items never picked in any prior run — drives item coverage testing)
                const header = 'run,character,build,survivalSec,level,floor,kills,killsPerMin,chests,gold,damageTaken,damageDealt,deathCause,anomalies,bugs,blineTriggers,trailTeleports,namedEliteFlees,trapMarks,enemyNearMissFrames,weaponNearMisses,noveltyPicks,botMs,enemiesMs,weaponsMs,itemsMs,picksTracked,brokenPicks,brokenPickNames,weapons';
                const rows = runs.map(r => {
                  const kpm = r.survivalTimeSec > 0 ? (r.killCount / r.survivalTimeSec * 60).toFixed(1) : '0';
                  const weapons = (r.weaponsUsed ?? []).join('|') || 'none';
                  const brokenNames = (r.brokenPickNames ?? []).join('|') || 'none';
                  // Normalize perf to ms per second of game time — comparable across runs of different lengths
                  const pb = r.perfBuckets;
                  const survSec = r.survivalTimeSec || 1;
                  const msPerSec = (raw: number | undefined) => raw !== undefined ? (raw / survSec).toFixed(2) : '0';
                  return [r.runNumber, r.characterId ?? 'unknown', r.buildType ?? 'adaptive', r.survivalTimeSec.toFixed(1), r.levelReached, r.floorReached,
                   r.killCount, kpm, r.chestsOpened, r.goldCollected, r.damageTaken.toFixed(0),
                   r.damageDealt.toFixed(0), r.deathCause,
                   r.anomalies.length, r.anomalies.filter(a => a.severity === 'bug').length,
                   r.blineTriggers ?? 0, r.trailTeleports ?? 0, r.namedEliteFlees ?? 0,
                   r.trapCellsMarked ?? 0, r.enemyNearMissFrames ?? 0, r.weaponNearMisses ?? 0,
                   r.noveltyPicks ?? 0,
                   msPerSec(pb?.bot), msPerSec(pb?.enemies), msPerSec(pb?.weapons), msPerSec(pb?.items),
                   r.totalPicksTracked ?? 0, r.brokenPickCount ?? 0, `"${brokenNames}"`,
                   `"${weapons}"`].join(',');
                });
                const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
                const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
                a.download = `bot_runs_${runs.length}_${Date.now()}.csv`; a.click();
              }}
              style={{ width: '100%', background: '#0a1210', border: '1px solid #0c4', color: '#0c4', borderRadius: 4, padding: '4px 0', fontFamily: 'monospace', fontSize: 11, cursor: 'pointer', marginTop: 4 }}
            >
              ⬇ DOWNLOAD BOT RUNS CSV ({getBotRunLogs().length} runs)
            </button>

            <SectionLabel>SESSION EVENT LOG ({log.length} events)</SectionLabel>
            <div style={{
              maxHeight: 190, overflowY: 'auto', background: '#080808',
              border: '1px solid #1e1e1e', borderRadius: 4, padding: '4px 6px',
            }}>
              {log.length === 0 && <div style={{ color: '#444', fontSize: 11 }}>No events yet — use other tabs to generate data.</div>}
              {[...log].reverse().slice(0, 60).map((e, i) => (
                <div key={i} style={{ borderBottom: '1px solid #111', padding: '2px 0', fontSize: 10 }}>
                  <span style={{ color: '#444' }}>{e.time}</span>
                  &nbsp;<span style={{ color: '#0cf' }}>{e.event}</span>
                  &nbsp;<span style={{ color: '#eee' }}>{e.value}</span>
                </div>
              ))}
            </div>

            <Btn label="⬇ DOWNLOAD SESSION CSV" color="#0a1a0a" textColor="#0f0" onClick={downloadDebugCsv} />
          </div>
        )}

      </div>
    </div>
  );
}
